import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import type { EntityRecord } from "@wedding/schemas";
import { Button, Card, Skeleton } from "@wedding/ui";
import {
  AlertTriangle,
  Check,
  Clock3,
  Maximize2,
  Phone,
  TimerReset,
  WifiOff,
  X,
} from "lucide-react";
import { api } from "../api";
import {
  enqueueOfflineMutation,
  getOffline,
  getOfflineMutations,
  saveOffline,
  saveOfflineMutations,
} from "../offline";
import { ModuleTodoSection } from "../components/ModuleTodoSection";

function minutes(value: unknown) {
  const [hours, mins] = String(value ?? "00:00")
    .split(":")
    .map(Number);
  return (hours ?? 0) * 60 + (mins ?? 0);
}

function addMinutes(value: unknown, amount: number) {
  const total = (minutes(value) + amount + 24 * 60) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

function localDate(timezone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export function SchedulePage() {
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId: string;
  };
  const queryClient = useQueryClient();
  const [runMode, setRunMode] = useState(false);
  const [view, setView] = useState<"records" | "tasks" | "library">("records");
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState(
    () => localStorage.getItem(`wedding-last-sync:${workspaceId}`) ?? "",
  );
  const [feedback, setFeedback] = useState("");
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [clock, setClock] = useState(() => new Date());
  const markSynced = useCallback(() => {
    const timestamp = new Date().toISOString();
    localStorage.setItem(`wedding-last-sync:${workspaceId}`, timestamp);
    setLastSyncedAt(timestamp);
  }, [workspaceId]);

  const workspace = useQuery({
    queryKey: [workspaceId, "workspace"],
    queryFn: () => api.workspace(workspaceId).then((result) => result.data),
  });
  const assignments = useQuery({
    queryKey: [workspaceId, "assignments"],
    queryFn: () =>
      api.list(workspaceId, "assignments").then((result) => result.data),
  });
  const query = useQuery({
    queryKey: [workspaceId, "schedules"],
    queryFn: async () => {
      try {
        const rows = (await api.list(workspaceId, "schedules")).data;
        await saveOffline(`schedule:${workspaceId}`, rows);
        markSynced();
        return rows;
      } catch (error) {
        const rows =
          (await getOffline<EntityRecord[]>(`schedule:${workspaceId}`)) ?? [];
        if (!rows.length) throw error;
        return rows;
      }
    },
  });

  const refreshPending = useCallback(async () => {
    const queue = await getOfflineMutations(workspaceId);
    setPendingCount(queue.length);
    window.dispatchEvent(
      new CustomEvent("wedding-sync-status", {
        detail: { pending: queue.length },
      }),
    );
  }, [workspaceId]);

  const syncPending = useCallback(async () => {
    if (!navigator.onLine) return;
    const queue = await getOfflineMutations(workspaceId);
    if (!queue.length) return;
    const remaining = [];
    for (const item of queue) {
      try {
        await api.update(
          workspaceId,
          item.entityType,
          item.entityId,
          item.data,
          item.expectedVersion,
        );
      } catch {
        try {
          const latest = (await api.list(workspaceId, "schedules")).data.find(
            (record) => record.id === item.entityId,
          );
          if (!latest) continue;
          if (item.data.completed && latest.data.completed) continue;
          await api.update(
            workspaceId,
            "schedules",
            item.entityId,
            item.data,
            latest.version,
          );
        } catch {
          remaining.push(item);
        }
      }
    }
    await saveOfflineMutations(workspaceId, remaining);
    setPendingCount(remaining.length);
    if (remaining.length === 0) {
      setFeedback("离线操作已全部同步");
      markSynced();
      await queryClient.invalidateQueries({
        queryKey: [workspaceId, "schedules"],
      });
    } else {
      setFeedback(`${remaining.length} 项操作尚未同步，请稍后重试`);
    }
  }, [markSynced, queryClient, workspaceId]);

  useEffect(() => {
    void refreshPending();
    const onOnline = () => {
      setIsOnline(true);
      void syncPending();
    };
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [refreshPending, syncPending]);

  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const rows = useMemo(
    () =>
      [...(query.data ?? [])].sort(
        (a, b) => minutes(a.data.startTime) - minutes(b.data.startTime),
      ),
    [query.data],
  );
  const timezone = workspace.data?.settings.timezone ?? "Asia/Shanghai";
  const weddingDate = workspace.data?.settings.weddingDate ?? "";
  const isWeddingDay = weddingDate === localDate(timezone);
  const currentMinutes = Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
      .format(clock)
      .replace(":", ""),
  );
  const currentTotal =
    Math.floor(currentMinutes / 100) * 60 + (currentMinutes % 100);
  let current = Math.max(
    0,
    rows.findIndex((item) => !item.data.completed),
  );
  if (isWeddingDay) {
    rows.forEach((item, index) => {
      if (minutes(item.data.startTime) <= currentTotal) current = index;
    });
  }

  const setCompletedLocally = useCallback(
    (item: EntityRecord) => {
      queryClient.setQueryData<EntityRecord[]>(
        [workspaceId, "schedules"],
        (old = []) =>
          old.map((row) =>
            row.id === item.id
              ? { ...row, data: { ...row.data, completed: true } }
              : row,
          ),
      );
    },
    [queryClient, workspaceId],
  );

  const complete = useMutation({
    mutationFn: async (item: EntityRecord) => {
      if (!navigator.onLine) throw new Error("offline");
      return api.update(
        workspaceId,
        "schedules",
        item.id,
        { completed: true },
        item.version,
      );
    },
    onMutate: setCompletedLocally,
    onSuccess: async () => {
      setFeedback("已标记完成");
      markSynced();
      await queryClient.invalidateQueries({
        queryKey: [workspaceId, "schedules"],
      });
    },
    onError: async (_error, item) => {
      if (navigator.onLine) {
        setFeedback(
          _error instanceof Error ? _error.message : "操作失败，请重试",
        );
        await queryClient.invalidateQueries({
          queryKey: [workspaceId, "schedules"],
        });
        return;
      }
      await enqueueOfflineMutation(workspaceId, {
        id: crypto.randomUUID(),
        entityType: "schedules",
        entityId: item.id,
        data: { completed: true },
        expectedVersion: item.version,
        createdAt: new Date().toISOString(),
      });
      setPendingCount((await getOfflineMutations(workspaceId)).length);
      setFeedback("已离线保存，联网后会自动同步");
    },
  });

  const delay = useMutation({
    mutationFn: (item: EntityRecord) =>
      api.update(
        workspaceId,
        "schedules",
        item.id,
        {
          startTime: addMinutes(item.data.startTime, 5),
          ...(item.data.endTime
            ? { endTime: addMinutes(item.data.endTime, 5) }
            : {}),
        },
        item.version,
      ),
    onSuccess: async () => {
      setFeedback("当前环节已顺延 5 分钟");
      markSynced();
      await queryClient.invalidateQueries({
        queryKey: [workspaceId, "schedules"],
      });
    },
  });

  const item = rows[current] ?? rows[0];
  const next = rows[current + 1];
  const owner = String(item?.data.owner ?? "");
  const ownerAssignment = assignments.data?.find((assignment) => {
    const name = String(assignment.data.name ?? "");
    const role = String(assignment.data.role ?? "");
    return owner && (name === owner || role === owner || name.includes(owner));
  });
  const ownerContact = String(ownerAssignment?.data.contact ?? "");

  const contactOwner = async () => {
    if (!ownerContact) {
      setFeedback(
        owner ? `尚未为“${owner}”填写联系方式` : "当前环节尚未指定负责人",
      );
      return;
    }
    try {
      await navigator.clipboard.writeText(ownerContact);
      setFeedback(`已复制 ${owner} 的联系方式：${ownerContact}`);
    } catch {
      setFeedback(`${owner} 的联系方式：${ownerContact}`);
    }
  };

  useEffect(() => {
    if (runMode) document.documentElement.requestFullscreen?.().catch(() => {});
    else if (document.fullscreenElement)
      document.exitFullscreen?.().catch(() => {});
  }, [runMode]);

  if (query.isLoading || workspace.isLoading) {
    return (
      <div className="page">
        <Skeleton lines={10} />
      </div>
    );
  }

  if (runMode) {
    return (
      <div className="run-mode">
        <header>
          <div>
            <span className="live-dot" />{" "}
            {isWeddingDay ? "婚礼当天运行中" : "流程预演中"}
          </div>
          <time>
            {clock.toLocaleTimeString("zh-CN", {
              hour: "2-digit",
              minute: "2-digit",
              timeZone: timezone,
            })}
          </time>
          <button onClick={() => setRunMode(false)} aria-label="退出运行模式">
            <X />
          </button>
        </header>
        {!isWeddingDay && (
          <div className="run-preview-notice">
            今天不是婚礼日期（{weddingDate}），当前按首个未完成环节进行预演。
          </div>
        )}
        <main>
          <aside>
            <span>当前环节</span>
            <strong>{String(item?.data.startTime ?? "")}</strong>
            <p>{String(item?.data.location ?? "")}</p>
          </aside>
          <section>
            <span className="eyebrow">当前进行</span>
            <h1>{String(item?.data.segment ?? "流程尚未开始")}</h1>
            <div className="run-details">
              <p>
                <small>负责人</small>
                <strong>{owner || "待确认"}</strong>
              </p>
              <p>
                <small>参与人员</small>
                <strong>{String(item?.data.participants ?? "—")}</strong>
              </p>
              <p>
                <small>所需物品</small>
                <strong>{String(item?.data.requiredItems ?? "—")}</strong>
              </p>
              <p className="risk">
                <small>风险预案</small>
                <strong>{String(item?.data.contingency ?? "无")}</strong>
              </p>
            </div>
            {emergencyOpen && (
              <div className="run-emergency" role="status">
                <strong>应急处理</strong>
                <p>
                  {String(
                    item?.data.contingency ??
                      "该环节尚未填写应急预案，请立即联系总协调人。",
                  )}
                </p>
              </div>
            )}
            {feedback && (
              <p className="run-feedback" role="status">
                {feedback}
              </p>
            )}
            <div className="run-actions">
              <Button
                onClick={() => item && complete.mutate(item)}
                disabled={!item || Boolean(item.data.completed)}
              >
                <Check /> {item?.data.completed ? "已完成" : "完成当前环节"}
              </Button>
              <Button
                className="secondary"
                onClick={() => item && delay.mutate(item)}
                disabled={!item || !isOnline || delay.isPending}
              >
                <TimerReset /> {delay.isPending ? "顺延中…" : "顺延 5 分钟"}
              </Button>
              <Button className="secondary" onClick={() => void contactOwner()}>
                <Phone /> 联系负责人
              </Button>
              <Button
                className="danger"
                aria-expanded={emergencyOpen}
                onClick={() => setEmergencyOpen((open) => !open)}
              >
                <AlertTriangle /> {emergencyOpen ? "收起预案" : "应急预案"}
              </Button>
            </div>
          </section>
          <aside className="next">
            <span>下一环节</span>
            <strong>{String(next?.data.startTime ?? "—")}</strong>
            <p>{String(next?.data.segment ?? "今日流程结束")}</p>
          </aside>
        </main>
        <footer className={isOnline ? "online" : "offline"}>
          <WifiOff size={16} />{" "}
          {!isOnline
            ? "当前离线；操作会安全保存在本机"
            : pendingCount
              ? `${pendingCount} 项操作等待同步`
              : "所有现场操作均已同步"}
          <span>
            最后同步：
            {lastSyncedAt
              ? new Date(lastSyncedAt).toLocaleTimeString("zh-CN", {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "尚未同步"}
          </span>
        </footer>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">婚礼当天</span>
          <h1>婚礼当天流程</h1>
          <p>运行模式支持离线完成、现场联系负责人和查看应急预案。</p>
        </div>
        {view === "records" && (
          <Button onClick={() => setRunMode(true)}>
            <Maximize2 size={17} />{" "}
            {isWeddingDay ? "进入当天模式" : "进入预演模式"}
          </Button>
        )}
      </header>
      {!isWeddingDay && view === "records" && (
        <div className="page-notice">
          婚礼日期是 {weddingDate}
          。现在进入会开启预演，不会把当前时间误判为婚礼进度。
        </div>
      )}
      {view === "records" && (
        <div className="sync-summary" role="status">
          <span>{isOnline ? "网络正常" : "当前离线"}</span>
          <span>{pendingCount} 项待上传</span>
          <span>
            最后同步：
            {lastSyncedAt
              ? new Date(lastSyncedAt).toLocaleString("zh-CN")
              : "尚未同步"}
          </span>
        </div>
      )}
      {feedback && (
        <div className="page-notice" role="status">
          {feedback}
        </div>
      )}
      <div
        className="workspace-view-tabs"
        role="tablist"
        aria-label="婚礼当天流程工作区"
      >
        <button
          className={view === "records" ? "active" : ""}
          onClick={() => setView("records")}
          role="tab"
          aria-selected={view === "records"}
        >
          当天流程清单
        </button>
        <button
          className={view === "tasks" ? "active" : ""}
          onClick={() => setView("tasks")}
          role="tab"
          aria-selected={view === "tasks"}
        >
          筹备事项
        </button>
        <button
          className={view === "library" ? "active" : ""}
          onClick={() => setView("library")}
          role="tab"
          aria-selected={view === "library"}
        >
          事项库
        </button>
      </div>
      {view === "records" ? (
        <div className="timeline-list">
          {rows.map((row) => (
            <Card key={row.id} className={row.data.completed ? "done" : ""}>
              <div className="timeline-time">
                <Clock3 size={16} />
                <strong>{String(row.data.startTime)}</strong>
              </div>
              <div className="timeline-line">
                <i />
              </div>
              <div className="timeline-content">
                <header>
                  <h3>{String(row.data.segment)}</h3>
                  {Boolean(row.data.completed) && (
                    <span>
                      <Check size={14} /> 已完成
                    </span>
                  )}
                </header>
                <p>
                  {String(row.data.location ?? "")} · 负责人：
                  {String(row.data.owner ?? "待定")}
                </p>
                <small>{String(row.data.precautions ?? "")}</small>
              </div>
              <button
                onClick={() => complete.mutate(row)}
                disabled={Boolean(row.data.completed)}
                aria-label={`${row.data.completed ? "已完成" : "标记完成"}：${String(row.data.segment)}`}
              >
                {row.data.completed ? "已完成" : "标记完成"}
              </button>
            </Card>
          ))}
        </div>
      ) : (
        <ModuleTodoSection
          workspaceId={workspaceId}
          moduleType="schedules"
          moduleLabel="婚礼当天流程"
          view={view === "library" ? "library" : "current"}
        />
      )}
    </div>
  );
}
